import { urlWebSocket } from '@/config/urlWebSocket'
import { isAuthenticated } from '@/event/account/auth/authentification'
import { idTimeout } from '@/event/account/idTimeout'
import { indexUpdateElement } from '@/event/cartBus'
import { showSectionManagement } from '@/event/main/app/showSectionManagement'
import { deliveryTimeOrder } from '@/event/order/data/deliveryTimeOrder'
import { newOrder } from '@/event/order/data/newOrder'
import { newVisibleOrder } from '@/event/order/data/newVisibleOrder'
import { mostRecentOrder } from '@/event/order/mostRecentOrder'
import { waitingEvent } from '@/event/order/success/waitingEvent'
import { productAssociated } from '@/event/product/productAssociated'
import { productBus } from '@/event/product/productBus'
import { showNextButtonBus } from '@/event/product/showNextButtonBus'
import { isOtherThemeHappy, isThemeDefault, isThemeHappy } from '@/event/theme/theme'
import { showHeader } from '@/happyTheme/event/layout/showHeader'
import { ShowModalDisconnect } from '@/happyTheme/event/modalAccount/disconnected/showModalDisconnect'
import { excludeRouteApp } from '@/storage/navbar/excludeRoute'
import { lastOrderId } from '@/storage/order/orderGet'
import products, { type Product } from '@/storage/products'
import stores from '@/storage/stores'
import { checkAndRefreshTokenV2 } from '@/utils/jwt/checkAndRefreshTokenV2'
import { loadData } from '@/utils/loadData'
import { catalogueBase, logo, settingNavbar } from '@/utils/main/navbar/statesNavbar'
import { buttonNextOption } from '@/utils/product/buttonNextOption'
import { useStoreInfo } from '@/utils/useStoreInfo'
import { io, type Socket } from 'socket.io-client'
import { watch } from 'vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

const resetIndexUpdateElement = () => {
  indexUpdateElement.value = null
}

const { testDay, testHour, testMinute, updateStore } = useStoreInfo()

export function watchersApp(route: RouteLocationNormalizedLoaded) {

  watch(
    () => productBus.value,
    () => {
      if (productBus.value) {
        buttonNextOption(productBus.value.options, showNextButtonBus)

        if (productBus?.value?.related_ids.length > 0) {
          productAssociated.value = productBus.value.related_ids.length
            ? products
              .getProductData()
              .filter((p: Product) =>
                productBus.value!.related_ids.includes(p.product_id)
              )
              .sort(() => Math.random() - 0.5)
              .slice(0, 4)
            : []

        } else {
          productAssociated.value = []
        }
      }
    }
  )

  watch(
    () => route.name,
    (newRoute, oldRoute) => {
      if (newRoute !== oldRoute
        && (isThemeHappy.value
          || isOtherThemeHappy.value)) {
        showHeader.value = true
      }

      if (isThemeHappy.value || isOtherThemeHappy.value) {
        if (newRoute && !isAuthenticated.value) {
          ShowModalDisconnect.value = false
        }
      } else if (isThemeDefault.value) {
        const oldRouteValid = oldRoute
        if (oldRoute === 'error_urlchercker') {
          loadData()
        }

        if (idTimeout.value && oldRoute) {
          if (excludeRouteApp.includes(String(oldRouteValid))) {
            clearTimeout(idTimeout.value)
            idTimeout.value = null
          }
        }

      }
    }
  )

  watch(
    () => route.params,
    (newParams) => {
      if (newParams.product_id === undefined) {
        resetIndexUpdateElement()
      }
    }
  )

  watch([testDay, testHour, testMinute], () => {
    const data: any = stores.getStoresData()
    if (data) {
      updateStore(data)
    }
  })

  watch(
    () => stores.getStoresData(),
    (newValue) => {
      if (newValue && newValue.setting) {
        settingNavbar.value = stores.getStoresData().setting
        const logoItem = settingNavbar.value.find(
          (item: { key: string }) => item.key === 'config_logo'
        ).value

        logo.value = logoItem ?? null
      }

      if (newValue) {
        catalogueBase.value = stores.getStoresData().category
      }
    },
    { immediate: true }
  )

  const socket: Socket = io(urlWebSocket)

  socket.connect()

  watch(route, (newRoute, oldRoute) => {
    if (newRoute.name === 'dashboard-order' || newRoute.name === "dashboard" || newRoute.name === 'success-payment') {
      if (lastOrderId.value) {
        newOrder.value = Number(lastOrderId.value)
      }
    }
  })

  watch(
    () => newOrder.value,
    (orderNew, orderOld) => {
      if (orderNew !== 0) {
        const order_id = orderNew
        socket.emit('join', order_id)
        socket.off('orderStatus').on('orderStatus', (data: any) => {
          // console.log(data)
          if (data.store_id === stores.state.storeData.store_id) {
            waitingEvent.value = true

            if (isThemeDefault.value) {
              if (
                data.order.order_info.order_id === mostRecentOrder.value?.order_id
              ) {
                mostRecentOrder.value = data.order.order_info
                newVisibleOrder.value = data.order.order_info
              } else {
                newVisibleOrder.value = null

              }
            }

            if (data.order.order_info.delivery_time !== null) {
              deliveryTimeOrder.value =
                data.order.order_info.delivery_time.replace(':', 'h')
            }

            if (isThemeHappy.value || isOtherThemeHappy.value) {
              checkAndRefreshTokenV2()
            }
          }
        })
      }
    }, { deep: true, immediate: true }
  )

  watch(
    () => stores.getStoresData(),
    (newStore, oldStore) => {
      if (newStore) {
        const storeId = newStore.store_id

        if (!socket.connected) {
          socket.connect()
        }

        if (socket) {
          socket.emit('create', storeId)
          socket.off('switch').on('switch', (data: { store_id: number | undefined; sw: string; value: number }) => {
            // console.log(data)
            if (data.store_id === storeId) {
              if (data.sw === 'maintenance') {
                if (data.value === 1) {
                  showSectionManagement.value = true
                } else {
                  showSectionManagement.value = false
                }
              }
            }
          })
        }
      }
    }
  )
}
